import { useEffect, useState } from 'react';

// Real URLs for PAI Academy (GSL-2). The rest of the shell navigates by state
// (navigationStore.currentPage); Academy is the one module whose places have to
// be linkable from an email, the maic Learn site or a chat, so it keeps its
// location in the path:
//
//   /academy                                     the learner's landing
//   /academy/tracks/<track>                      one certification
//   /academy/tracks/<track>/lessons/<lesson>     one lesson, open
//   /academy/tracks/<track>/exam                 the practical exam
//   /academy/programs/<program>                  one program
//   /academy/programs/<program>/exam             its certification exam (GSL-4)
//   /academy/master                              Maestro Pastelero PAI: stages and state
//   /academy/master/e1 | e2 | e3 | e4            one stage's sitting (E1 written exam, E2 lab, E3 diagnosis, E4 red team)
//   /academy/grading[/<item>]                    evaluators' grading queue / one item
//   /academy/catalog                             public catalog (GSL-3)
//   /academy/catalog/<track>                     public syllabus of a track
//   /academy/community                           learner community (GSL-1)
//   /academy/community/tracks/<track>            questions about a course
//   /academy/community/tracks/<t>/lessons/<l>    ... about one of its lessons
//   /academy/community/programs/<program>[/lessons/<l>]   same, for programs
//   /academy/community/general                   questions about no course
//   /academy/community/.../ask                   ask, with that subject chosen
//   /academy/community/questions/<id>            one question and its answers
//
// The catalog is public (outside the auth gate); everything else needs a
// session and falls to the login page first, keeping the URL.

/** What a community page is about: a course (and maybe one lesson) or none. */
export type CommunityScope =
  | { kind: 'track' | 'program'; key: string; lesson?: string }
  | { kind: 'general' };

/** Stage sittings with their own screen: E1 (written exam), E3 and E4 (platform stages). */
export type MasterStageKey = 'e1' | 'e2' | 'e3' | 'e4';
const MASTER_STAGES: readonly string[] = ['e1', 'e2', 'e3', 'e4'];

export type AcademyRoute =
  | { view: 'community'; scope?: CommunityScope; question?: string; ask?: boolean }
  | { view: 'home' }
  | { view: 'course'; track: string; lesson?: string; exam?: boolean }
  | { view: 'program'; program: string; exam?: boolean }
  | { view: 'catalog'; track?: string }
  | { view: 'master'; stage?: MasterStageKey }
  | { view: 'grading'; item?: string };

export const ACADEMY_ROOT = '/academy';
const NAV_EVENT = 'academy:navigate';

const seg = (s: string) => encodeURIComponent(s);

export function academyPath(route: AcademyRoute): string {
  switch (route.view) {
    case 'home':
      return ACADEMY_ROOT;
    case 'course': {
      const base = `${ACADEMY_ROOT}/tracks/${seg(route.track)}`;
      if (route.lesson) return `${base}/lessons/${seg(route.lesson)}`;
      return route.exam ? `${base}/exam` : base;
    }
    case 'program':
      return `${ACADEMY_ROOT}/programs/${seg(route.program)}${route.exam ? '/exam' : ''}`;
    case 'master':
      return route.stage ? `${ACADEMY_ROOT}/master/${seg(route.stage)}` : `${ACADEMY_ROOT}/master`;
    case 'grading':
      return route.item ? `${ACADEMY_ROOT}/grading/${seg(route.item)}` : `${ACADEMY_ROOT}/grading`;
    case 'catalog':
      return route.track ? `${ACADEMY_ROOT}/catalog/${seg(route.track)}` : `${ACADEMY_ROOT}/catalog`;
    case 'community': {
      const base = `${ACADEMY_ROOT}/community`;
      if (route.question) return `${base}/questions/${seg(route.question)}`;
      return `${base}${scopePath(route.scope)}${route.ask ? '/ask' : ''}`;
    }
  }
}

function scopePath(scope?: CommunityScope): string {
  if (!scope) return '';
  if (scope.kind === 'general') return '/general';
  const base = `/${scope.kind === 'track' ? 'tracks' : 'programs'}/${seg(scope.key)}`;
  return scope.lesson ? `${base}/lessons/${seg(scope.lesson)}` : base;
}

function parseCommunity(parts: string[]): AcademyRoute {
  if (parts[0] === 'questions' && parts[1]) return { view: 'community', question: parts[1] };
  const ask = parts[parts.length - 1] === 'ask';
  const rest = ask ? parts.slice(0, -1) : parts;
  const [kind, key, sub, lesson] = rest;
  let scope: CommunityScope | undefined;
  if (kind === 'general' && rest.length === 1) scope = { kind: 'general' };
  else if ((kind === 'tracks' || kind === 'programs') && key) {
    const k = kind === 'tracks' ? 'track' : 'program';
    scope = sub === 'lessons' && lesson ? { kind: k, key, lesson } : { kind: k, key };
  }
  const route: AcademyRoute = { view: 'community' };
  if (scope) route.scope = scope;
  if (ask) route.ask = true;
  return route;
}

/** The Academy route a path names, or null when it is not an Academy path.
 *  An Academy path that names nothing known falls back to the landing. */
export function parseAcademyPath(pathname: string): AcademyRoute | null {
  const clean = pathname.replace(/\/+$/, '');
  if (clean !== ACADEMY_ROOT && !clean.startsWith(`${ACADEMY_ROOT}/`)) return null;
  let parts: string[];
  try {
    parts = clean.slice(ACADEMY_ROOT.length).split('/').filter(Boolean).map(decodeURIComponent);
  } catch {
    return { view: 'home' };
  }
  const [kind, key, sub, subKey] = parts;
  if (kind === 'community') return parseCommunity(parts.slice(1));
  if (kind === 'master' && !key) return { view: 'master' };
  if (kind === 'master') return MASTER_STAGES.includes(key) ? { view: 'master', stage: key as MasterStageKey } : { view: 'home' };
  if (kind === 'programs' && key === 'maestro_pastelero_pai' && !sub) return { view: 'master' };
  if (kind === 'grading') return key ? { view: 'grading', item: key } : { view: 'grading' };
  if (kind === 'catalog') return key ? { view: 'catalog', track: key } : { view: 'catalog' };
  if (kind === 'programs' && key) return sub === 'exam' ? { view: 'program', program: key, exam: true } : { view: 'program', program: key };
  if (kind === 'tracks' && key) {
    if (sub === 'lessons' && subKey) return { view: 'course', track: key, lesson: subKey };
    if (sub === 'exam') return { view: 'course', track: key, exam: true };
    return { view: 'course', track: key };
  }
  return { view: 'home' };
}

export function isAcademyPath(pathname: string): boolean {
  return parseAcademyPath(pathname) !== null;
}

/** The public catalog renders outside the auth gate. */
export function isPublicCatalogPath(pathname: string): boolean {
  return parseAcademyPath(pathname)?.view === 'catalog';
}

/** Move to an Academy route: a new history entry (or a replaced one), and a
 *  notice to every useAcademyRoute. Same-path calls are no-ops. */
export function goAcademy(route: AcademyRoute, opts: { replace?: boolean } = {}): void {
  const path = academyPath(route);
  if (window.location.pathname === path) return;
  if (opts.replace) window.history.replaceState(window.history.state, '', path);
  else window.history.pushState(null, '', path);
  window.dispatchEvent(new Event(NAV_EVENT));
}

/** The current Academy route, following pushes and the back/forward buttons.
 *  Off an Academy path (a unit test, a harness) it reads as the landing. */
export function useAcademyRoute(): AcademyRoute {
  const read = () => parseAcademyPath(window.location.pathname) ?? { view: 'home' as const };
  const [route, setRoute] = useState<AcademyRoute>(read);
  useEffect(() => {
    const sync = () => setRoute(read());
    window.addEventListener('popstate', sync);
    window.addEventListener(NAV_EVENT, sync);
    return () => {
      window.removeEventListener('popstate', sync);
      window.removeEventListener(NAV_EVENT, sync);
    };
  }, []);
  return route;
}

/** Open an Academy route from outside the Academy page (the shell's help
 *  menu): a new history entry plus the popstate the shell and the Academy
 *  both follow, so the page switches and lands on that route. */
export function openAcademy(route: AcademyRoute): void {
  const path = academyPath(route);
  if (window.location.pathname !== path) window.history.pushState(null, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

/** A plain click goes through goAcademy; a modified click (new tab, copy
 *  link) keeps the browser's own behavior, which is why these are anchors. */
export function onAcademyLinkClick(route: AcademyRoute) {
  return (e: { button?: number; metaKey?: boolean; ctrlKey?: boolean; shiftKey?: boolean; altKey?: boolean; preventDefault: () => void }) => {
    if ((e.button ?? 0) !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    goAcademy(route);
  };
}

/** The full URL of a route, to copy and share. */
export function academyUrl(route: AcademyRoute): string {
  return `${window.location.origin}${academyPath(route)}`;
}
